import type { StyleSpecification } from 'maplibre-gl';

// Environment-specific configuration.
//
// Note: there is NO authToken here. Authentication flows through the
// session cookie set by the BFF after login — the browser bundle never
// holds a Basic-auth credential. (An earlier version of this file
// committed a shared Basic token per environment; that field is gone,
// the credential has been rotated out of use, and consumers should go
// through `apiClient` + the session cookie.)
interface EnvironmentConfig {
  apiUrl: string;
  oldAppUrl: string;
}

const ENVIRONMENT_CONFIGS: Record<string, EnvironmentConfig> = {
  dev: {
    apiUrl: import.meta.env.VITE_API_DEV_URL,
    oldAppUrl: "https://qa-react.ecolane.com/",
  },
  qa: {
    apiUrl: import.meta.env.VITE_API_QA_URL,
    oldAppUrl: "https://qa-react.ecolane.com/",
  },
  demo: {
    apiUrl: import.meta.env.VITE_API_DEMO_URL,
    oldAppUrl: "https://demo-react.ecolane.com/",
  },
  prod: {
    apiUrl: import.meta.env.VITE_API_PROD_URL || import.meta.env.VITE_API_QA_URL,
    oldAppUrl: "https://prod-dispatcher.ecolane.com/",
  },
  localhost: {
    apiUrl: "http://localhost:3000",
    oldAppUrl: "https://qa-react.ecolane.com/",
  },
};

// Detect current environment based on hostname
const getCurrentEnvironment = (): EnvironmentConfig => {
  if (typeof window === 'undefined') {
    // Default to QA during SSR or non-browser context
    return ENVIRONMENT_CONFIGS.qa;
  }

  const hostname = window.location.hostname;

  if (hostname.includes('dev-dispatcher.ecolane.com') || hostname.includes('dev-react.ecolane.com')) {
    return ENVIRONMENT_CONFIGS.demo;
  } else if (hostname.includes('qa-dispatcher.ecolane.com') || hostname.includes('qa-react.ecolane.com')) {
    return ENVIRONMENT_CONFIGS.qa;
  } else if (hostname.includes('demo-dispatcher.ecolane.com') || hostname.includes('demo-react.ecolane.com')) {
    return ENVIRONMENT_CONFIGS.demo;
  } else if (hostname.includes('prod-dispatcher.ecolane.com') || hostname.includes('prod-react.ecolane.com')) {
    return ENVIRONMENT_CONFIGS.prod;
  } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return ENVIRONMENT_CONFIGS.localhost;
  }

  // Default to QA for any other environment
  return ENVIRONMENT_CONFIGS.qa;
};

const currentEnv = getCurrentEnvironment();

// Centralized environment configuration
export const env = {
  API_URL: currentEnv.apiUrl,
  OLD_APP_URL: currentEnv.oldAppUrl,
  /**
   * True only in a production Vite build (NODE_ENV=production).
   * Used to gate analytics/tracking (Pendo) so it never fires in dev/QA.
   * Equivalent to legacy `Eco::PROD_ENV` check in analytics_support.rb.
   */
  IS_PRODUCTION: import.meta.env.PROD,
  // Injected at build time by CodePipeline via:
  //   export VITE_APP_VERSION="${tag}" && npm run build
  // Falls back to "development" in local dev (no CI build step).
  APP_VERSION: import.meta.env.VITE_APP_VERSION || "development",
  MAP_STYLE_URL: import.meta.env.VITE_MAP_STYLE_URL || "INLINE_STYLE",
  NOMINATIM_URL:
    import.meta.env.VITE_NOMINATIM_URL || "https://nominatim.openstreetmap.org",
  /**
   * Base URL for the real-time vehicle telemetry SSE service.
   * Injected at build time via VITE_TELEMETRY_SSE_BASE_URL.
   *
   * Dev default: "" (empty string — same-origin, no prefix needed).
   * The Vite proxy in vite.config.ts intercepts /runtime/* and forwards
   * it server-side to the HTTP ALB, avoiding Mixed Content errors caused
   * by an HTTPS page opening an HTTP EventSource directly.
   *
   * The resulting URL:
   *   /runtime/sse/tenant?tenantId={tenantId}
   * which Vite proxies as-is to the ALB.
   *
   * In production: set VITE_TELEMETRY_SSE_BASE_URL to the HTTPS ALB or
   * a *.ecolane.com subdomain (e.g. https://telemetry.ecolane.com).
   */
  TELEMETRY_SSE_BASE_URL:
    import.meta.env.VITE_TELEMETRY_SSE_BASE_URL || "",
  NODE_ENV: import.meta.env.NODE_ENV,
  DEV_MODE: import.meta.env.DEV,
  // NOTE: the Google Maps browser key is deliberately NOT here any more.
  // It came from VITE_GOOGLE_MAPS_API_KEY and was therefore compiled into a
  // publicly downloadable bundle, harvestable without signing in and rotatable
  // only by rebuilding and redeploying every environment. It is now fetched at
  // runtime from the session-guarded GET /drt/api/bff/maps-config — see
  // shared/api/mapsConfigApi.ts and shared/providers/GoogleMapsProvider.tsx.
  //
  // This did not make the key secret: the Maps JS SDK runs in the page, so any
  // signed-in user can still read it from devtools. The Google Cloud HTTP
  // referrer allowlist, API allowlist and quota cap remain the real control on
  // abuse and must stay configured per environment.
} as const;

// Google Maps standard road-map tile style (green terrain, matches the app reference screenshots)
export const GOOGLE_MAPS_STYLE: StyleSpecification = {
  version: 8 as const,
  name: "Google Maps",
  sources: {
    google: {
      type: "raster" as const,
      tiles: [
        "https://mt0.google.com/vt/lyrs=m&x={x}&y={y}&z={z}",
        "https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}",
        "https://mt2.google.com/vt/lyrs=m&x={x}&y={y}&z={z}",
        "https://mt3.google.com/vt/lyrs=m&x={x}&y={y}&z={z}",
      ],
      tileSize: 256,
      attribution: "Map data ©2026 Google",
    },
  },
  layers: [
    {
      id: "google",
      type: "raster" as const,
      source: "google",
    },
  ],
};

// Google Maps satellite tile style
export const GOOGLE_SATELLITE_STYLE: StyleSpecification = {
  version: 8 as const,
  name: "Google Satellite",
  sources: {
    google: {
      type: "raster" as const,
      tiles: [
        "https://mt0.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
        "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
        "https://mt2.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
        "https://mt3.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
      ],
      tileSize: 256,
      attribution: "Map data ©2026 Google",
    },
  },
  layers: [
    {
      id: "google",
      type: "raster" as const,
      source: "google",
    },
  ],
};

// Map style with OpenStreetMap tiles for actual street/geography visibility
export const INLINE_MAP_STYLE = {
  version: 8 as const,
  name: "OpenStreetMap",
  sources: {
    osm: {
      type: "raster" as const,
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
    },
  },
  layers: [
    {
      id: "osm",
      type: "raster" as const,
      source: "osm",
    },
  ],
};

// Type for environment variables
export type Environment = typeof env;

// Never include in bundles:
// - Database credentials
// - API keys for external services
// - Authentication tokens
// - Backend service URLs (use public endpoints only)
